// ============================================================================
// useNotYetBadgeCount.js
//
// Real-time Firestore listener that counts referrals in the "Not Yet" inbox.
//
// SINGLE SOURCE OF TRUTH GUARANTEE:
//   Two listeners matching the exact same filters as NotYetInboxView:
//     1. outcome.status == 'not_yet'                   (clinical NOT_YET)
//     2. workflowStatus == 'NEEDS_INFO'
//        AND outcome.status in ['routine','urgent']
//        AND awaitingGpDocs == true                     (ops NEEDS_INFO w/ open docs)
//   Both scoped to createdByUid == uid for gp_employee.
//   The awaitingGpDocs filter keeps the count in lockstep with the inbox: Not-Yet
//   recovery referrals (NEEDS_INFO awaiting intake review, no document requests)
//   are excluded from both. The badge therefore equals the inbox row count.
//
// NO PHI. Reads only from tenants/{tenantId}/referrals (non-PHI collection).
// FAIL-SAFE. On any query failure: logs error, returns 0, badge hides.
// ============================================================================

import { useEffect, useState } from 'react';
import {
  getFirestore,
  collection,
  query,
  where,
  onSnapshot,
} from 'firebase/firestore';
import { getApp } from 'firebase/app';
import { PATHS } from '../core/firestorePaths.js';

/**
 * Returns the live count of referrals visible in the Not Yet inbox.
 * Includes both NOT_YET outcome referrals and NEEDS_INFO ops referrals.
 *
 * @param {object} params
 * @param {'gp_employee'|'intake_coordinator'|string} params.role
 * @param {string|null} params.tenantId
 * @param {string|null} params.uid  — Firebase UID of the current user
 * @returns {number}  0 when hidden, positive integer when there are items
 */
export function useNotYetBadgeCount({ role, tenantId, uid }) {
  const [notYetCount, setNotYetCount]       = useState(0);
  const [needsInfoCount, setNeedsInfoCount] = useState(0);

  // Listener 1: outcome.status == 'not_yet' (clinical NOT_YET)
  useEffect(() => {
    if (role !== 'gp_employee' && role !== 'intake_coordinator') return;
    if (!tenantId || !uid) return;

    const db     = getFirestore(getApp());
    const colRef = collection(db, PATHS.col.referrals(tenantId));

    const constraints = [where('outcome.status', '==', 'not_yet')];

    if (role === 'gp_employee') {
      constraints.push(where('createdByUid', '==', uid));
    }

    const q = query(colRef, ...constraints);

    const unsub = onSnapshot(
      q,
      (snap) => setNotYetCount(snap.size),
      (err) => {
        console.error(
          '[useNotYetBadgeCount] not_yet snapshot error — badge hidden.',
          'code:', err.code,
          'message:', err.message,
        );
        setNotYetCount(0);
      },
    );

    return unsub;
  }, [role, tenantId, uid]);

  // Listener 2: workflowStatus == 'NEEDS_INFO' AND outcome.status in ['routine','urgent']
  useEffect(() => {
    if (role !== 'gp_employee' && role !== 'intake_coordinator') return;
    if (!tenantId || !uid) return;

    const db     = getFirestore(getApp());
    const colRef = collection(db, PATHS.col.referrals(tenantId));

    const constraints = [
      where('workflowStatus', '==', 'NEEDS_INFO'),
      where('outcome.status', 'in', ['routine', 'urgent']),
      // Match NotYetInboxView exactly: only referrals with >=1 open document
      // request (excludes Not-Yet recovery referrals awaiting intake review).
      where('awaitingGpDocs', '==', true),
    ];

    if (role === 'gp_employee') {
      constraints.push(where('createdByUid', '==', uid));
    }

    const q = query(colRef, ...constraints);

    const unsub = onSnapshot(
      q,
      (snap) => setNeedsInfoCount(snap.size),
      (err) => {
        console.error(
          '[useNotYetBadgeCount] needs_info snapshot error — badge hidden.',
          'code:', err.code,
          'message:', err.message,
        );
        setNeedsInfoCount(0);
      },
    );

    return unsub;
  }, [role, tenantId, uid]);

  return notYetCount + needsInfoCount;
}
